import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Check,
  X,
  Edit3,
  Loader2,
  Clock,
  Camera,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Eye,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { screenCaptureApi, mattersApi } from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";

// ─── Types ──────────────────────────────────────────────────────────────────

interface TimeBlock {
  id: string;
  matterId: string;
  matterNumber?: string;
  matterTitle?: string;
  clientName?: string;
  narrative: string;
  hours: number;
  startTime: string;
  endTime: string;
  captureCount: number;
  confidence: number;
  billable: boolean;
  matterReasoning?: string;
  narrativeReasoning?: string;
}

interface Props {
  sessionId: string;
  onClose: () => void;
  onAccepted: () => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
    cents / 100
  );

const formatTime = (iso: string) => {
  try {
    return new Date(iso).toLocaleTimeString("en-CA", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
};

const MATTER_COLORS = [
  "#0F5132", "#1976D2", "#7B1FA2", "#C62828", "#EF6C00",
  "#00838F", "#4527A0", "#2E7D32", "#AD1457", "#283593",
];

function matterColor(matterId: string): string {
  let hash = 0;
  for (let i = 0; i < matterId.length; i++) {
    hash = (hash << 5) - hash + matterId.charCodeAt(i);
  }
  return MATTER_COLORS[Math.abs(hash) % MATTER_COLORS.length];
}

function confidenceConfig(value: number) {
  if (value >= 80)
    return {
      label: `${value}%`,
      colorClass: "bg-emerald-100 text-emerald-700 border-emerald-200",
      dotColor: "bg-emerald-500",
    };
  if (value >= 50)
    return {
      label: `${value}%`,
      colorClass: "bg-amber-100 text-amber-700 border-amber-200",
      dotColor: "bg-amber-500",
    };
  return {
    label: `${value}%`,
    colorClass: "bg-red-50 text-red-600 border-red-200",
    dotColor: "bg-red-500",
  };
}

// ─── Component ──────────────────────────────────────────────────────────────

const ScreenCaptureReview: React.FC<Props> = ({
  sessionId,
  onClose,
  onAccepted,
}) => {
  const [blocks, setBlocks] = useState<TimeBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [captures, setCaptures] = useState<any[]>([]);
  const [matters, setMatters] = useState<any[]>([]);
  const [sessionStats, setSessionStats] = useState<{
    duration: string;
    totalCaptures: number;
    mattersDetected: number;
  }>({ duration: "0:00", totalCaptures: 0, mattersDetected: 0 });

  // Per-block state
  const [acceptedIds, setAcceptedIds] = useState<Set<string>>(new Set());
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ narrative: "", hours: 0, matterId: "", billable: true });
  const [expandedMatterReason, setExpandedMatterReason] = useState<Set<string>>(new Set());
  const [expandedNarrativeReason, setExpandedNarrativeReason] = useState<Set<string>>(new Set());

  const loadSession = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [sessionRes, mattersRes] = await Promise.all([
        screenCaptureApi.session(sessionId),
        mattersApi.list({ limit: 200, status: "open" }),
      ]);
      const data = sessionRes.data;
      // Captures come from session endpoint; blocks come from generate endpoint
      setCaptures(data?.captures || []);
      const captureList = data?.captures || [];
      const nonSkipped = captureList.filter((c: any) => !c.skipped);
      const uniqueMatters = new Set(nonSkipped.map((c: any) => c.analysis?.detectedMatterId).filter(Boolean));
      // Compute duration from first/last capture
      let duration = "0:00";
      if (captureList.length >= 2) {
        const first = new Date(captureList[captureList.length - 1].capturedAt).getTime();
        const last = new Date(captureList[0].capturedAt).getTime();
        const mins = Math.round((last - first) / 60000);
        duration = `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, "0")}`;
      }
      setSessionStats({
        duration: data?.session?.duration || duration,
        totalCaptures: captureList.length,
        mattersDetected: uniqueMatters.size,
      });
      setMatters(mattersRes.data?.data || []);
    } catch (err: any) {
      console.error("Failed to load session:", err);
      setError("Could not load session data. The session may no longer exist.");
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const generateBlocks = useCallback(async () => {
    try {
      setGenerating(true);
      setError(null);
      const genRes = await screenCaptureApi.generate(sessionId);
      const generatedBlocks = genRes.data?.blocks || [];
      // Map backend blocks to frontend TimeBlock format
      setBlocks(
        generatedBlocks.map((b: any, i: number) => ({
          id: b.matterId ? `${b.matterId}-${i}` : `block-${i}`,
          matterId: b.matterId || null,
          matterNumber: b.matterNumber || "",
          matterTitle: b.matterTitle || "Unassigned",
          clientName: b.clientName || "",
          narrative: b.narrative || "Work performed",
          hours: (b.durationMinutes || 6) / 60,
          startTime: b.startTime,
          endTime: b.endTime,
          captureCount: b.captureCount || 1,
          confidence: Math.round((b.confidence || 0) * 100),
          billable: b.billable !== false,
          matterReasoning: b.matterReason || "",
          narrativeReasoning: b.narrativeReason || "",
        }))
      );
    } catch (err) {
      console.error("Failed to generate blocks:", err);
    } finally {
      setGenerating(false);
    }
    // Load session data (captures with thumbnails, stats)
    await loadSession();
  }, [sessionId, loadSession]);

  useEffect(() => {
    generateBlocks();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const matterOptions: SelectOption[] = matters.map((m: any) => ({
    value: m._id,
    label: `${m.matterNumber ? m.matterNumber + " — " : ""}${m.title}`,
  }));

  const visibleBlocks = blocks.filter(
    (b) => !acceptedIds.has(b.id) && !dismissedIds.has(b.id)
  );

  const totalHours = visibleBlocks.reduce((sum, b) => sum + b.hours, 0);
  const estimatedValueCents = totalHours * 25000; // rough $250/hr estimate

  const handleAcceptBlock = (block: TimeBlock) => {
    setAcceptedIds((prev) => new Set(prev).add(block.id));
  };

  const handleDismissBlock = (blockId: string) => {
    setDismissedIds((prev) => new Set(prev).add(blockId));
  };

  const startEditing = (block: TimeBlock) => {
    setEditingId(block.id);
    setEditForm({
      narrative: block.narrative,
      hours: block.hours,
      matterId: block.matterId,
      billable: block.billable,
    });
  };

  const saveEdit = (blockId: string) => {
    setBlocks((prev) =>
      prev.map((b) =>
        b.id === blockId
          ? { ...b, narrative: editForm.narrative, hours: editForm.hours, matterId: editForm.matterId, billable: editForm.billable }
          : b
      )
    );
    setEditingId(null);
  };

  const handleAcceptAll = async () => {
    try {
      setAccepting(true);
      const blocksToAccept = visibleBlocks.map((b) => ({
        ...b,
        // merge any edits
      }));
      await screenCaptureApi.accept({ sessionId, blocks: blocksToAccept });
      onAccepted();
    } catch (err) {
      console.error("Failed to accept blocks:", err);
    } finally {
      setAccepting(false);
    }
  };

  const handleAcceptSelected = async () => {
    try {
      setAccepting(true);
      const blocksToAccept = blocks.filter((b) => acceptedIds.has(b.id));
      await screenCaptureApi.accept({ sessionId, blocks: blocksToAccept });
      onAccepted();
    } catch (err) {
      console.error("Failed to accept selected blocks:", err);
    } finally {
      setAccepting(false);
    }
  };

  const toggleReasonSection = (
    blockId: string,
    setter: React.Dispatch<React.SetStateAction<Set<string>>>
  ) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  };

  // ─── Loading / Generating state ─────────────────────────────────────────────

  if (loading || generating) {
    return (
      <div className="py-16 flex flex-col items-center justify-center">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center mb-4">
          <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800 mb-2">
          {generating ? "AI is analyzing your captures..." : "Loading session..."}
        </h3>
        {generating && (
          <p className="text-sm text-slate-500 text-center max-w-sm">
            Identifying matters, matching clients, and crafting professional
            billing narratives from your screen activity.
          </p>
        )}
        {generating && (
          <div className="mt-6 w-64">
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"
                initial={{ width: "5%" }}
                animate={{ width: "90%" }}
                transition={{ duration: 12, ease: "easeOut" }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── Error state ─────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="py-16 flex flex-col items-center justify-center">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
          <Camera className="w-8 h-8 text-red-300" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800 mb-2">
          Session unavailable
        </h3>
        <p className="text-sm text-slate-500 text-center max-w-sm mb-4">
          {error}
        </p>
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
        >
          Close
        </button>
      </div>
    );
  }

  // ─── Empty state ────────────────────────────────────────────────────────────

  if (blocks.length === 0) {
    return (
      <div className="py-16 flex flex-col items-center justify-center">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
          <Camera className="w-8 h-8 text-slate-300" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800 mb-2">
          No time blocks generated
        </h3>
        <p className="text-sm text-slate-500 text-center max-w-sm mb-4">
          The AI couldn&apos;t identify distinct work activities from the captured
          screenshots. This can happen with very short sessions.
        </p>
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
        >
          Close
        </button>
      </div>
    );
  }

  // ─── Review cards ───────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      {/* Session stats header */}
      <div className="flex items-center gap-6 px-1">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{sessionStats.duration}</span> duration
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{sessionStats.totalCaptures}</span> captures
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{sessionStats.mattersDetected}</span> matters detected
          </span>
        </div>
      </div>

      {/* Time block cards */}
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        <AnimatePresence mode="popLayout">
          {visibleBlocks.map((block, idx) => {
            const conf = confidenceConfig(block.confidence);
            const color = block.matterId ? matterColor(block.matterId) : "#94a3b8";
            const isEditing = editingId === block.id;

            return (
              <motion.div
                key={block.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -60, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
                layout
                className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm"
              >
                {/* Matter color bar */}
                <div className="h-1" style={{ backgroundColor: color }} />

                {/* Screenshot thumbnails */}
                {(() => {
                  const blockCaptures = captures.filter((c: any) => {
                    if (!c.thumbnail) return false;
                    const t = new Date(c.capturedAt).getTime();
                    const start = new Date(block.startTime).getTime() - 5000;
                    const end = new Date(block.endTime).getTime() + 35000;
                    return t >= start && t <= end;
                  });
                  if (blockCaptures.length === 0) return null;
                  return (
                    <div className="flex gap-1.5 p-3 pb-0 overflow-x-auto">
                      {blockCaptures.map((c: any, ci: number) => (
                        <img
                          key={ci}
                          src={`data:image/jpeg;base64,${c.thumbnail}`}
                          alt={`Capture ${ci + 1}`}
                          className="h-16 w-auto rounded-lg border border-slate-200 flex-shrink-0 object-cover opacity-90 hover:opacity-100 hover:border-emerald-400 transition-all cursor-pointer"
                          title={`${new Date(c.capturedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })} — ${c.analysis?.description || "Screen capture"}`}
                        />
                      ))}
                    </div>
                  );
                })()}

                <div className="p-5">
                  {/* Top row: matter + hours */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <SearchableSelect
                          options={matterOptions}
                          value={editForm.matterId}
                          onChange={(val) =>
                            setEditForm((prev) => ({ ...prev, matterId: val || "" }))
                          }
                          placeholder="Select matter..."
                        />
                      ) : (
                        <>
                          <p className="text-sm font-semibold text-slate-800">
                            {block.matterNumber && (
                              <span className="text-slate-400 mr-1.5">
                                {block.matterNumber}
                              </span>
                            )}
                            {block.matterTitle || "Unknown Matter"}
                          </p>
                          {block.clientName && (
                            <p className="text-xs text-slate-400 mt-0.5">
                              Client: {block.clientName}
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-2 ml-3">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={editForm.hours}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              hours: Math.max(0.1, parseFloat(e.target.value) || 0.1),
                            }))
                          }
                          className="w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-right font-semibold focus:outline-none focus:border-emerald-500"
                        />
                      ) : (
                        <span className="text-lg font-bold text-slate-800">
                          {block.hours.toFixed(1)}
                          <span className="text-xs font-normal text-slate-400 ml-1">hrs</span>
                        </span>
                      )}
                      {!isEditing && (
                        <button
                          onClick={() => startEditing(block)}
                          className="p-1 rounded-md text-slate-300 hover:text-slate-500 hover:bg-slate-50 transition-colors"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Narrative */}
                  {isEditing ? (
                    <textarea
                      value={editForm.narrative}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, narrative: e.target.value }))
                      }
                      rows={3}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 resize-none mb-3"
                    />
                  ) : (
                    <p className="text-sm text-slate-600 leading-relaxed mb-4 italic">
                      &ldquo;{block.narrative}&rdquo;
                    </p>
                  )}

                  {/* AI Reasoning sections */}
                  {!isEditing && (
                    <div className="space-y-2 mb-4">
                      {/* Why this matter? */}
                      {block.matterReasoning && (
                        <div>
                          <button
                            onClick={() =>
                              toggleReasonSection(block.id, setExpandedMatterReason)
                            }
                            className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            {expandedMatterReason.has(block.id) ? (
                              <ChevronUp className="w-3 h-3" />
                            ) : (
                              <ChevronDown className="w-3 h-3" />
                            )}
                            Why this matter?
                          </button>
                          <AnimatePresence>
                            {expandedMatterReason.has(block.id) && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="mt-2 p-3 bg-slate-50 border border-slate-100 rounded-lg text-xs text-slate-500 leading-relaxed">
                                  {block.matterReasoning}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}

                      {/* Why this narrative? */}
                      {block.narrativeReasoning && (
                        <div>
                          <button
                            onClick={() =>
                              toggleReasonSection(block.id, setExpandedNarrativeReason)
                            }
                            className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            {expandedNarrativeReason.has(block.id) ? (
                              <ChevronUp className="w-3 h-3" />
                            ) : (
                              <ChevronDown className="w-3 h-3" />
                            )}
                            Why this narrative?
                          </button>
                          <AnimatePresence>
                            {expandedNarrativeReason.has(block.id) && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="mt-2 p-3 bg-slate-50 border border-slate-100 rounded-lg text-xs text-slate-500 leading-relaxed">
                                  {block.narrativeReasoning}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Meta row */}
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        {formatTime(block.startTime)} - {formatTime(block.endTime)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                        <Camera className="w-3 h-3" />
                        {block.captureCount} captures
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${conf.colorClass}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${conf.dotColor}`} />
                        Confidence: {conf.label}
                      </span>

                      {/* Billable toggle */}
                      {isEditing ? (
                        <button
                          onClick={() =>
                            setEditForm((prev) => ({ ...prev, billable: !prev.billable }))
                          }
                          className="inline-flex items-center gap-1 text-xs"
                        >
                          {editForm.billable ? (
                            <ToggleRight className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <ToggleLeft className="w-4 h-4 text-slate-400" />
                          )}
                          <span className={editForm.billable ? "text-emerald-600 font-medium" : "text-slate-400"}>
                            {editForm.billable ? "Billable" : "Non-billable"}
                          </span>
                        </button>
                      ) : (
                        <span
                          className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                            block.billable
                              ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                              : "bg-slate-100 text-slate-400"
                          }`}
                        >
                          {block.billable ? "Billable" : "Non-billable"}
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1.5">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => saveEdit(block.id)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                          >
                            <Check className="w-3.5 h-3.5" />
                            Save
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleDismissBlock(block.id)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                            Dismiss
                          </button>
                          <button
                            onClick={() => startEditing(block)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleAcceptBlock(block)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                          >
                            <Check className="w-3.5 h-3.5" />
                            Accept
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Bottom summary + actions */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <div className="flex items-center gap-4">
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{visibleBlocks.length}</span> entries
            {" \u00B7 "}
            <span className="font-semibold text-slate-800">{totalHours.toFixed(1)}</span> total hours
            {" \u00B7 "}
            <span className="font-semibold text-emerald-700">
              {formatCurrency(estimatedValueCents)}
            </span>{" "}
            estimated value
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="text-xs text-slate-400 hover:text-red-500 transition-colors"
          >
            Dismiss All
          </button>
          {acceptedIds.size > 0 && (
            <button
              onClick={handleAcceptSelected}
              disabled={accepting}
              className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-colors disabled:opacity-50"
            >
              {accepting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="w-3.5 h-3.5" />
              )}
              Accept Selected ({acceptedIds.size})
            </button>
          )}
          <button
            onClick={handleAcceptAll}
            disabled={accepting || visibleBlocks.length === 0}
            className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-bold text-white bg-[#0F5132] hover:bg-[#146C43] rounded-xl transition-colors disabled:opacity-50 shadow-sm"
          >
            {accepting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScreenCaptureReview;
